import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { useShallow } from 'zustand/react/shallow'
import { upload } from 'api/aws'
import {
	copyContent,
	createContentUpload,
	editContentAsset,
	partialContentUpdate,
	replaceAsset,
} from 'api/content'
import { multiRelationUpdate } from 'api/relations'
import { setReferenceToContent } from 'api/chat'
import Sidebar from 'components/Sidebar'
import FileUpload from 'components/FileUpload'
import SidebarTabs from 'components/Sidebar/SidebarTabs'
import Info from './components/Info'
import Video from './components/Video'
import useRelationsContent from 'hooks/api/useRelationsContent'
import { t } from 'helpers'
import { getUser } from 'helpers/storage'
import { imageUploadWorkflowId, videoUploadWorkflowId } from 'helpers/constants'
import { getConsoleDuration, getVideoDuration } from 'helpers/getVideoMetadata'
import { useAppStore, useCastingStore } from 'store'
import { RELATIONS } from 'types/enums'
import { notif } from 'helpers/notif'
import { Content } from 'types/app'

const tabs = [
	{
		label: 'APP_UPLOAD_SCENE_FILE',
		value: 0,
	},
	{
		label: 'APP_CHOOSE_SCENE_FILE',
		value: 1,
	},
	// {
	// label: 'APP_RECORD_SCENE_FILE',
	// value: 2,
	// },
]

const SceneUploadSidebar = (): JSX.Element => {
	const [loading, setLoading] = useState(false)
	const { roleId } = useParams()
	const swiperRef = useRef<SwiperClass>()
	const [activeTab, setActiveTab] = useState(tabs[0].value)
	const documentViewerOpened = useAppStore(
		useShallow((state) => !!state.documentViewerSidebar?.link),
	)
	const [scene, uploadedContent, setOpenSidebar] = useCastingStore(
		useShallow((state) => [
			state.sceneUploadContent,
			state.uploadedSceneContent,
			state.setSceneUploadContent,
		]),
	)
	const sceneType =
		scene &&
		Object.values(scene).some((v) => {
			const value = v.toString().toLowerCase()

			return value.includes('photo') || value.includes('image')
		})
			? 'Image'
			: 'Video'

	const { data: d } = useRelationsContent(
		roleId && !!scene ? [RELATIONS.CONTENT] : [],
		`${roleId}_Content_Access`,
		'Relationship',
	)

	const playlistContent = d?.[RELATIONS.CONTENT]?.find(
		(content) => content?.ContentOwnerId === getUser()?.['Custom:User:Id'],
	)

	const { data } = useRelationsContent([
		RELATIONS.BEST_PERFORMANCES,
		RELATIONS.IMAGE,
	])
	const showreelz = data?.[RELATIONS.BEST_PERFORMANCES] || []
	const images = data?.[RELATIONS.IMAGE] || []

	const onChange = async (
		file: File | string,
		type: 'Image' | 'Video',
		duration?: number,
	): Promise<void> => {
		const sceneDuration = scene?.Duration ? +scene?.Duration : 0
		if (file instanceof File) {
			if (type === 'Image') {
				save(file, type)
			} else {
				const duration = await getVideoDuration(file)
				const fileDuration = duration ? +duration : 0
				if (
					fileDuration > 0 &&
					sceneDuration > 0 &&
					fileDuration <= sceneDuration
				) {
					save(file, type, fileDuration)
				} else {
					notif('error', t('APP_SCENE_DURATION_FILE_ERROR'))
					const input = document.getElementById(
						'scenevideo_' + scene?.ContentId,
					) as HTMLInputElement
					if (input) input.value = ''
				}
			}
		} else {
			if (type === 'Image') {
				save(file, type)
			} else {
				if (
					duration &&
					duration > 0 &&
					sceneDuration > 0 &&
					duration <= sceneDuration
				) {
					save(file, type, duration)
				} else {
					notif('error', t('APP_SCENE_DURATION_FILE_ERROR'))
					const input = document.getElementById(
						'scenevideo_' + scene?.ContentId,
					) as HTMLInputElement
					if (input) input.value = ''
				}
			}
		}
	}

	const reuploadUpdate = async (content: Content): Promise<void> => {
		try {
			const resubmitCount = content?.ContentCastingResubmit
				? +content?.ContentCastingResubmit
				: 0
			await partialContentUpdate(content.ContentId, {
				properties: {
					'Content:Casting:Reupload': false,
					'Content:Casting:Reupload:Timestamp': Date.now(),
					'Content:Casting:Resubmit': resubmitCount + 1,
				},
			})
		} catch (error) {
			console.log('error:', error)
		}
	}

	const save = async (
		file: File | string,
		type: 'Image' | 'Video',
		duration?: number,
	): Promise<void> => {
		try {
			setLoading(true)
			if (!scene?.ContentId) return

			const reupload = uploadedContent?.ContentCastingReupload === 'true'

			if (typeof file !== 'string') {
				const uploadedFile = await upload({
					fileObj: file,
					assetType: 'Content',
				})
				let content = null

				const uploadData = {
					...uploadedFile,
					originalTitle: file.name,
					workflowId:
						type === 'Image' ? imageUploadWorkflowId : videoUploadWorkflowId,
					originalLanguage: 'en-US',
					localizations: {
						'en-US': { name: uploadedFile.originalFileName },
					},
					type: type,
					originalFileName: uploadedFile.originalFileName,
					objectUrl: uploadedFile.fileAssetUrl,
					publicUrl: uploadedFile.cdnUrl,
					duration: getConsoleDuration(duration || 0),
					exhibitionWindow: {
						'--': {
							availableFrom:
								uploadedFile.availableFrom?.toISOString &&
								uploadedFile.availableFrom.toISOString(),
							availableUntil:
								uploadedFile.availableUntil?.toISOString &&
								uploadedFile.availableUntil.toISOString(),
						},
					},
					allowMinting: true,
					allowRemix: true,
					allowComments: true,
					published: true,
				}

				if (playlistContent?.ContentId && uploadedContent?.ContentId) {
					content = await editContentAsset(
						{
							type: type,
							isPublic: true,
							objectUrl: uploadedFile.fileAssetUrl,
							publicUrl: uploadedFile.cdnUrl,
							locale: 'en-US',
							originalFileName: uploadedFile.originalFileName,
							releaseDate: uploadedFile.availableFrom?.toISOString(),
							uploadedByUser: true,
						},
						uploadedContent?.ContentId,
					)
					if (reupload) reuploadUpdate(uploadedContent)
				} else content = await createContentUpload(uploadData)

				if (!uploadedContent) {
					setReferenceToContent(
						RELATIONS.REFERENCE,
						content.id,
						scene.ContentId,
					)

					multiRelationUpdate(scene.ContentId, content.id)

					if (playlistContent)
						multiRelationUpdate(
							playlistContent?.ContentId,
							content.id,
							// RELATIONS.PLAYLIST,
							// scene['Globalizationen-USContentTitle'],
							// scene?.relationIndex,
						)
				}
			} else {
				if (uploadedContent?.ContentId) {
					await replaceAsset(uploadedContent.ContentId, file)
					if (reupload) reuploadUpdate(uploadedContent)
				} else {
					const content = await copyContent(file, {
						keepAssets: true,
						ownerId: uploadedContent?.ContentOwnerId || '',
					})
					if (content?.id) {
						setReferenceToContent(
							RELATIONS.REFERENCE,
							content.id,
							scene.ContentId,
						)

						multiRelationUpdate(scene.ContentId, content?.id)

						if (playlistContent)
							multiRelationUpdate(
								playlistContent.ContentId,
								content?.id,
								// RELATIONS.PLAYLIST,
								// scene['Globalizationen-USContentTitle'],
								// scene?.relationIndex,
							)
					} else notif('error', t('APP_SCENE_UPLOADED_FAILED'))
				}
			}

			const { setUploadedSceneIds } = useCastingStore.getState()
			if (duration) setUploadedSceneIds(scene?.ContentId, duration)
			notif('success', t('APP_SCENE_UPLOADED_SUCCESSFULLY'))
		} catch (error) {
			console.log('error:', error)
		} finally {
			setLoading(false)
			setOpenSidebar(null)
			setActiveTab(0)
		}
	}

	useEffect(() => {
		if (!scene?.ContentId) setActiveTab(tabs?.[0].value)
	}, [scene?.ContentId])

	const onClose = (): void => {
		setOpenSidebar(null)
		const { setDocumentViewerSidebar } = useAppStore.getState()
		setDocumentViewerSidebar(null)
	}

	return (
		<Sidebar
			open={!!scene?.ContentId}
			onClose={onClose}
			iconClose={documentViewerOpened}
			header={{
				title: t('APP_SCENE_UPLOAD_SIDEBAR_TITLE').replace(
					'{SceneName}',
					scene?.['Globalizationen-USContentTitle'] || '',
				),
				description:
					sceneType === 'Image'
						? 'APP_SCENE_UPLOAD_PHOTO_SIDEBAR_DESCRIPTION'
						: 'APP_SCENE_UPLOAD_SIDEBAR_DESCRIPTION',
			}}
			loading={loading}
			buttons={[
				{
					children: t('APP_DONE'),
					onClick: onClose,
				},
			]}>
			<>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
					<Info scene={scene} sceneType={sceneType} />

					{uploadedContent ? <Video content={uploadedContent} /> : null}

					<SidebarTabs
						active={activeTab}
						options={tabs}
						onChange={(v) => {
							setActiveTab(v as number)
							swiperRef.current?.slideTo(v as number)
						}}
					/>
					{scene?.ContentId ? (
						<Swiper
							spaceBetween={10}
							onSwiper={(swiper: SwiperClass) => {
								swiperRef.current = swiper
							}}
							allowTouchMove={false}
							style={{ width: '100%', height: 'inherit' }}>
							<SwiperSlide>
								<FileUpload
									fileType={sceneType === 'Image' ? 'Image' : 'Video'}
									label={
										sceneType === 'Image'
											? 'APP_SCENE_PHOTO_LABEL'
											: 'APP_SCENE_VIDEO_LABEL'
									}
									form="rectangle-h"
									direction="vertical"
									itemTitle=""
									hintText=""
									name="scene_video"
									formatsText={
										sceneType === 'Image'
											? 'APP_PHOTO_FORMATS'
											: 'APP_SHOWREELS_FORMATS'
									}
									qualityText={
										sceneType === 'Image'
											? 'APP_PROFILEPICTURE_QUALITY'
											: 'APP_SHOWREELS_QUALITY'
									}
									fileInputProps={{
										id: 'scenevideo_' + scene?.ContentId,
										accept: sceneType === 'Image' ? 'Image/*' : 'video/*',
									}}
									value={''}
									onChange={(e) => {
										if (e.target.files && e.target.files[0])
											onChange(e.target.files[0], sceneType)
									}}
								/>
							</SwiperSlide>
							<SwiperSlide>
								<div className="grid2column">
									{(sceneType === 'Image' ? images : showreelz)?.map(
										(content) => (
											<div
												onClick={() =>
													onChange(
														content.ContentId,
														sceneType,
														content.ContentDuration
															? +(+content.ContentDuration / 1000).toFixed(0)
															: 0,
													)
												}
												key={content.ContentId}
												style={{
													width: '100%',
													height:
														'calc((var(--sidebarWidth) - 88px) / 2 / var(--videoAspect))',
													cursor: 'pointer',
													overflow: 'hidden',
													borderRadius: '15px',
												}}>
												<img
													src={
														content.ContentSquareImageUrl || '/placeholder.png'
													}
													alt={content.ContentTitle}
													style={{ width: '100%', height: '100%' }}
												/>
											</div>
										),
									)}
								</div>
							</SwiperSlide>
							{/* <SwiperSlide>
							{activeTab === 2 ? <RecordScene /> : null}
						</SwiperSlide> */}
						</Swiper>
					) : null}
				</div>
			</>
		</Sidebar>
	)
}

export default SceneUploadSidebar
